<template>
    <v-container class="reinigungsplan-container">
      <!-- Verwaltungs-Buttons -->
      <div class="management-buttons mb-4 no-print">
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="kcBereichDialog = true"
          class="mr-2"
        >
          KC-Bereich hinzufügen
        </v-btn>
        <v-btn
          color="secondary"
          prepend-icon="mdi-cog"
          @click="openArbeitsbereichDialog"
          :disabled="!activeKCData"
        >
          Arbeitsbereiche verwalten
        </v-btn>
      </div>

      <!-- Tab-Navigation für KC-Bereiche (dynamisch) -->
      <v-tabs v-model="activeKC" class="mb-4 no-print" v-if="kcBereiche.length > 0">
        <v-tab
          v-for="kc in kcBereiche"
          :key="kc.id"
          :value="kc.id"
        >
          {{ kc.name }} ({{ getArbeitsbereichCount(kc.id) }} Bereiche)
        </v-tab>
      </v-tabs>

      <v-card class="reinigungsplan-card mb-4" v-if="activeKCData">
        <v-card-title class="d-flex align-center print-title">
          <v-icon color="primary" size="24" class="mr-2 no-print">mdi-broom</v-icon>
          <span class="mr-2">{{ activeKCData.name }}</span>
          <span class="print-only kc-print-value">{{ activeKCData.kc_nummer }}</span>

          <v-spacer></v-spacer>

          <!-- Informationen zur aktuellen Woche -->
          <div class="week-info">
            <span class="year-display">Jahr: {{ currentYear }}</span>
            <span class="week-display ml-4">KW: {{ currentWeek }}</span>
          </div>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="mt-4">
          <!-- Dynamische Reinigungsplan-Tabelle -->
          <div ref="pdfContent" class="table-container">
            <table class="reinigungsplan-table" v-if="activeKCData && currentArbeitsbereiche.length > 0">
              <thead>
                <tr>
                  <th class="arbeitsbereich-header">Arbeitsbereich</th>
                  <th v-for="(day, index) in weekDays" :key="index" class="tag-header">
                    {{ day.name }}<br>
                    <span class="date-display">{{ day.date }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="bereich in currentArbeitsbereiche" :key="bereich.id">
                  <td class="arbeitsbereich-cell">
                    {{ bereich.name }}
                    <span v-if="bereich.mitarbeiter_nummer" class="mitarbeiter-info">
                      ({{ bereich.mitarbeiter_nummer }})
                    </span>
                  </td>
                  <td v-for="(day, dayIndex) in weekDays" :key="`day-${dayIndex}-${bereich.id}`" class="content-cell">
                    <input
                      type="text"
                      class="cell-input"
                      :id="`field-${activeKC}-${bereich.id}-${dayIndex}`"
                      :value="getReinigungsEintrag(bereich.id, day.name)"
                      @input="updateReinigungsEintrag(bereich.id, day.name, day.date, $event.target.value)"
                      @blur="saveReinigungsEintrag(bereich.id, day.name, day.date)"
                    >
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-else-if="activeKCData && currentArbeitsbereiche.length === 0" class="no-data">
              <p>Keine Arbeitsbereiche für diesen KC-Bereich vorhanden.</p>
              <v-btn @click="openArbeitsbereichDialog" color="primary">
                Arbeitsbereiche hinzufügen
              </v-btn>
            </div>
          </div>
          <div class="signature-section mt-6 print-only">
            <p class="signature-hint">
              Mit Ihrer Unterschrift bestätigen Sie, dass die Reinigungstätigkeiten entsprechend dem Plan durchgeführt wurden.
            </p>
          </div>
        </v-card-text>
      </v-card>

      <!-- PDF Export-Buttons -->
      <div class="export-buttons no-print" v-if="activeKCData">
        <v-btn
          color="primary"
          size="large"
          class="export-btn mr-4"
          prepend-icon="mdi-file-pdf-box"
          @click="exportPdf"
          :loading="exporting"
        >
          {{ activeKCData.name }} als PDF
        </v-btn>

        <v-btn
          color="success"
          size="large"
          class="export-btn"
          prepend-icon="mdi-file-multiple"
          @click="exportAllKCsPdf"
          :loading="exportingAll"
        >
          Alle KC als PDF
        </v-btn>
      </div>

      <!-- KC-Bereich Verwaltungsdialog -->
      <v-dialog v-model="kcBereichDialog" max-width="1000px">
        <v-card>
          <v-card-title>
            <span class="text-h5">KC-Bereiche verwalten</span>
          </v-card-title>
          <v-card-text>
            <!-- Neuen KC-Bereich hinzufügen -->
            <v-expansion-panels class="mb-4">
              <v-expansion-panel>
                <v-expansion-panel-title>
                  <v-icon class="mr-2">mdi-plus</v-icon>
                  Neuen KC-Bereich hinzufügen
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-form ref="kcForm" v-model="kcFormValid">
                    <v-row>
                      <v-col cols="6">
                        <v-text-field
                          v-model="kcBereichForm.kc_nummer"
                          label="KC-Nummer"
                          :rules="[v => !!v || 'KC-Nummer ist erforderlich']"
                          required
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="kcBereichForm.name"
                          label="Name"
                          :rules="[v => !!v || 'Name ist erforderlich']"
                          required
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="kcBereichForm.beschreibung"
                          label="Beschreibung"
                          rows="3"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                    <v-btn 
                      color="primary" 
                      @click="saveKCBereich" 
                      :disabled="!kcFormValid"
                      class="mt-2"
                    >
                      KC-Bereich hinzufügen
                    </v-btn>
                  </v-form>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Übersichtstabelle aller KC-Bereiche -->
            <v-divider class="my-4"></v-divider>
            <h4 class="mb-3">Vorhandene KC-Bereiche ({{ kcBereiche.length }}):</h4>
            
            <v-table v-if="kcBereiche.length > 0" class="kc-bereiche-table">
              <thead>
                <tr>
                  <th style="width: 150px;">KC-Nummer</th>
                  <th style="width: 200px;">Name</th>
                  <th>Beschreibung</th>
                  <th style="width: 120px; text-align: center;">Bereiche</th>
                  <th style="width: 100px; text-align: center;">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="kc in kcBereiche" :key="kc.id">
                  <td style="padding: 8px;">
                    <v-text-field
                      v-model="kc.kc_nummer"
                      density="compact"
                      variant="outlined"
                      hide-details
                      @blur="updateKCBereichField(kc)"
                    ></v-text-field>
                  </td>
                  <td style="padding: 8px;">
                    <v-text-field
                      v-model="kc.name"
                      density="compact"
                      variant="outlined"
                      hide-details
                      @blur="updateKCBereichField(kc)"
                    ></v-text-field>
                  </td>
                  <td style="padding: 8px;">
                    <v-text-field
                      v-model="kc.beschreibung"
                      density="compact"
                      variant="outlined"
                      hide-details
                      @blur="updateKCBereichField(kc)"
                    ></v-text-field>
                  </td>
                  <td style="text-align: center; padding: 8px;">
                    <v-chip size="small" color="primary">
                      {{ getArbeitsbereichCount(kc.id) }}
                    </v-chip>
                  </td>
                  <td style="text-align: center; padding: 8px;">
                    <v-btn
                      icon="mdi-delete"
                      size="small"
                      @click="deleteKCBereich(kc.id)"
                      color="error"
                      variant="text"
                    ></v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <v-alert v-else type="info" variant="tonal" class="mt-4">
              Noch keine KC-Bereiche vorhanden. Fügen Sie welche hinzu!
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" variant="text" @click="closeKCDialog">
              Schließen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Arbeitsbereich Verwaltungsdialog -->
      <v-dialog v-model="arbeitsbereichDialog" max-width="1200px">
        <v-card>
          <v-card-title>
            <span class="text-h5">Arbeitsbereiche verwalten - {{ activeKCData?.name }}</span>
          </v-card-title>
          <v-card-text>
            <!-- Neuer Arbeitsbereich hinzufügen -->
            <v-expansion-panels class="mb-4">
              <v-expansion-panel>
                <v-expansion-panel-title>
                  <v-icon class="mr-2">mdi-plus</v-icon>
                  Neuen Arbeitsbereich hinzufügen
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-form ref="arbeitsbereichForm" v-model="arbeitsbereichFormValid" v-if="arbeitsbereichForm">
                    <v-row>
                      <v-col cols="6">
                        <v-select
                          v-model="arbeitsbereichForm.arbeitsbereich_typ_id"
                          :items="arbeitsbereichTypen"
                          item-title="name"
                          item-value="id"
                          label="Arbeitsbereich-Typ"
                          :rules="[v => !!v || 'Typ ist erforderlich']"
                          required
                        ></v-select>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model.number="arbeitsbereichForm.nummer"
                          label="Nummer"
                          type="number"
                          :rules="[v => !!v || 'Nummer ist erforderlich']"
                          required
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="arbeitsbereichForm.name"
                          label="Name"
                          :rules="[v => !!v || 'Name ist erforderlich']"
                          required
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-select
                          v-model="selectedMitarbeiter"
                          :items="mitarbeiterItems"
                          item-title="display_name"
                          item-value="mitarbeiternummer"
                          label="Zugewiesener Mitarbeiter"
                          clearable
                          placeholder="Kein Mitarbeiter zugewiesen"
                          :clear-icon="'mdi-close'"
                          @update:model-value="onMitarbeiterSelectionChange"
                        >
                        </v-select>
                      </v-col>
                    </v-row>
                    <v-btn 
                      color="primary" 
                      @click="saveArbeitsbereich" 
                      :disabled="!arbeitsbereichFormValid"
                      class="mt-2"
                    >
                      Arbeitsbereich hinzufügen
                    </v-btn>
                  </v-form>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Übersichtstabelle aller Arbeitsbereiche -->
            <v-divider class="my-4"></v-divider>
            <h4 class="mb-3">Vorhandene Arbeitsbereiche ({{ currentArbeitsbereiche.length }}):</h4>
            
            <v-table v-if="currentArbeitsbereiche.length > 0" class="arbeitsbereiche-table">
              <thead>
                <tr>
                  <th style="width: 100px;">Nr.</th>
                  <th style="width: 220px;">Name</th>
                  <th style="width: 200px;">Typ</th>
                  <th>Zugewiesener Mitarbeiter</th>
                  <th style="width: 150px; text-align: center;">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="bereich in currentArbeitsbereiche" :key="bereich.id">
                  <td style="padding: 8px;">
                    <v-text-field
                      v-model="bereich.nummer"
                      type="number"
                      density="compact"
                      variant="outlined"
                      hide-details
                      style="min-width: 80px;"
                      @change="updateArbeitsbereichField(bereich)"
                    ></v-text-field>
                  </td>
                  <td style="padding: 8px;">
                    <v-text-field
                      v-model="bereich.name"
                      density="compact"
                      variant="outlined"
                      hide-details
                      @blur="updateArbeitsbereichField(bereich)"
                    ></v-text-field>
                  </td>
                  <td style="padding: 8px;">
                    <v-select
                      v-model="bereich.arbeitsbereich_typ_id"
                      :items="arbeitsbereichTypen"
                      item-title="name"
                      item-value="id"
                      density="compact"
                      variant="outlined"
                      hide-details
                      @update:model-value="updateArbeitsbereichField(bereich)"
                    ></v-select>
                  </td>
                  <td style="padding: 8px;">
                    <v-select
                      v-model="bereich.mitarbeiter_nummer"
                      :items="mitarbeiterItems"
                      item-title="display_name"
                      item-value="mitarbeiternummer"
                      density="compact"
                      variant="outlined"
                      clearable
                      placeholder="Kein Mitarbeiter"
                      hide-details
                      @update:model-value="updateArbeitsbereichField(bereich)"
                    >
                    </v-select>
                  </td>
                  <td style="text-align: center; padding: 8px;">
                    <v-btn
                      icon="mdi-delete"
                      size="small"
                      @click="deleteArbeitsbereich(bereich.id)"
                      color="error"
                      variant="text"
                    ></v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <v-alert v-else type="info" variant="tonal" class="mt-4">
              Noch keine Arbeitsbereiche vorhanden. Fügen Sie welche hinzu!
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" variant="text" @click="closeArbeitsbereichDialog">
              Schließen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </template>

  <script setup>
  import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
  import html2canvas from 'html2canvas';
  import { jsPDF } from 'jspdf';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import { API_BASE_URL } from '../config';

  // Toast-Instanz für Benachrichtigungen
  const toast = useToast();

  // Reactive Variablen
  const activeKC = ref(null);
  const pdfContent = ref(null);
  const exporting = ref(false);
  const exportingAll = ref(false);

  // Daten für KC-Bereiche und Arbeitsbereiche
  const kcBereiche = ref([]);
  const currentArbeitsbereiche = ref([]);
  const arbeitsbereichTypen = ref([]);
  const mitarbeiter = ref([]);
  const reinigungsEintraege = ref({});
  const kcArbeitsbereicheCounts = ref({}); // Speichert die Anzahl der Arbeitsbereiche pro KC

  // Dialog-Zustände
  const kcBereichDialog = ref(false);
  const arbeitsbereichDialog = ref(false);

  // Formulardaten
  const kcBereichForm = ref({
    id: null,
    kc_nummer: '',
    name: '',
    beschreibung: ''
  });

  const arbeitsbereichForm = ref({
    id: null,
    kc_bereich_id: null,
    arbeitsbereich_typ_id: null,
    nummer: null,
    name: '',
    mitarbeiter_nummer: null,
    sortierung: null
  });

  // Formularvalidierung
  const kcFormValid = ref(false);
  const arbeitsbereichFormValid = ref(false);
  const editingKCBereich = ref(false);
  const editingArbeitsbereich = ref(false);

  // Separate Ref für die Mitarbeiter-Auswahl (für bessere Kontrolle)
  const selectedMitarbeiter = ref(null);

  // Parameter für den Reinigungsplan
  const currentYear = ref(new Date().getFullYear());
  const currentWeek = ref(getWeekNumber(new Date()));

  // Computed Properties
  const activeKCData = computed(() => {
    return kcBereiche.value.find(kc => kc.id === activeKC.value) || null;
  });

  // Mitarbeiter-Liste mit "Kein Mitarbeiter" Option
  const mitarbeiterItems = computed(() => {
    const items = [
      {
        mitarbeiternummer: null,
        display_name: 'Kein Mitarbeiter zugewiesen'
      },
      ...mitarbeiter.value
    ];
    return items;
  });

  // Berechnet die Kalenderwoche
  function getWeekNumber(date) {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  // Berechnet die Tage der aktuellen Woche (Mo-Sa)
  const weekDays = computed(() => {
    const days = [];
    const now = new Date();
    const currentDay = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - (currentDay === 0 ? 6 : currentDay - 1));

    for (let i = 0; i < 6; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);

      days.push({
        name: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][i],
        date: formatDate(day),
      });
    }

    return days;
  });

  // Formatiert das Datum im DD.MM.YYYY Format
  function formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  // API-Aufrufe
  async function fetchKCBereiche() {
    try {
      const response = await axios.get(`${API_BASE_URL}reinigungsplan.php?action=get_kc_bereiche`, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        kcBereiche.value = response.data.data;

        // Für jeden KC-Bereich die Anzahl der Arbeitsbereiche laden
        for (const kc of kcBereiche.value) {
          try {
            const arbeitsbereicheResponse = await axios.get(`${API_BASE_URL}reinigungsplan.php?action=get_kc_arbeitsbereiche&kc_bereich_id=${kc.id}`, {
              withCredentials: true,
              headers: { "Content-Type": "application/json" },
            });

            if (arbeitsbereicheResponse.data.success) {
              kcArbeitsbereicheCounts.value[kc.id] = arbeitsbereicheResponse.data.data.length;
            } else {
              kcArbeitsbereicheCounts.value[kc.id] = 0;
            }
          } catch (error) {
            console.error(`Fehler beim Laden der Arbeitsbereiche für KC ${kc.id}:`, error);
            kcArbeitsbereicheCounts.value[kc.id] = 0;
          }
        }

        if (kcBereiche.value.length > 0 && !activeKC.value) {
          activeKC.value = kcBereiche.value[0].id;
        }
      } else {
        toast.error('Fehler beim Laden der KC-Bereiche: ' + response.data.message);
      }
    } catch (error) {
      console.error('Fehler beim Laden der KC-Bereiche:', error);
      toast.error('Fehler beim Laden der KC-Bereiche');
    }
  }

  async function fetchArbeitsbereichTypen() {
    try {
      const response = await axios.get(`${API_BASE_URL}reinigungsplan.php?action=get_arbeitsbereich_typen`, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        arbeitsbereichTypen.value = response.data.data;
      }
    } catch (error) {
      console.error('Fehler beim Laden der Arbeitsbereich-Typen:', error);
    }
  }

  async function fetchMitarbeiter() {
    try {
      const response = await axios.get(`${API_BASE_URL}reinigungsplan.php?action=get_mitarbeiter`, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        mitarbeiter.value = response.data.data.map(m => ({
          ...m,
          display_name: `${m.mitarbeiternummer} - ${m.vorname} ${m.nachname}`
        }));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  }

  async function fetchKCArbeitsbereiche(kcId) {
    if (!kcId) return;

    try {
      const response = await axios.get(`${API_BASE_URL}reinigungsplan.php?action=get_kc_arbeitsbereiche&kc_bereich_id=${kcId}`, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        // Stelle sicher, dass arbeitsbereich_typ_id gesetzt ist
        currentArbeitsbereiche.value = response.data.data.map(bereich => {
          // Wenn arbeitsbereich_typ_id fehlt, versuche sie aus dem Typ-Namen zu ermitteln
          if (!bereich.arbeitsbereich_typ_id && bereich.arbeitsbereich_typ) {
            bereich.arbeitsbereich_typ_id = getArbeitsbereichTypIdByName(bereich.arbeitsbereich_typ);
          }
          return bereich;
        });
        // Aktualisiere auch den Count für diesen KC-Bereich
        kcArbeitsbereicheCounts.value[kcId] = response.data.data.length;
        await fetchReinigungsplanData(kcId);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Arbeitsbereiche:', error);
    }
  }

  async function fetchReinigungsplanData(kcId) {
    if (!kcId) return;

    try {
      const response = await axios.get(`${API_BASE_URL}reinigungsplan.php?action=get_reinigungsplan_data&kc_bereich_id=${kcId}&kalenderwoche=${currentWeek.value}&jahr=${currentYear.value}`, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        // Daten in ein Object umwandeln für einfacheren Zugriff
        const entriesMap = {};
        response.data.data.forEach(entry => {
          const key = `${entry.kc_arbeitsbereich_id}-${entry.tag_name}`;
          entriesMap[key] = entry.eintrag || '';
        });
        reinigungsEintraege.value = entriesMap;
      }
    } catch (error) {
      console.error('Fehler beim Laden der Reinigungsplan-Daten:', error);
    }
  }

  // Hilfsfunktionen für Reinigungseinträge
  function getReinigungsEintrag(arbeitsbereichId, tagName) {
    const key = `${arbeitsbereichId}-${tagName}`;
    return reinigungsEintraege.value[key] || '';
  }

  function updateReinigungsEintrag(arbeitsbereichId, tagName, tagDatum, value) {
    const key = `${arbeitsbereichId}-${tagName}`;
    reinigungsEintraege.value[key] = value;
  }

  async function saveReinigungsEintrag(arbeitsbereichId, tagName, tagDatum) {
    const eintrag = getReinigungsEintrag(arbeitsbereichId, tagName);

    try {
      const response = await axios.post(`${API_BASE_URL}reinigungsplan.php`, {
        action: 'save_reinigungsplan_entry',
        kc_arbeitsbereich_id: arbeitsbereichId,
        tag_name: tagName,
        tag_datum: tagDatum,
        kalenderwoche: currentWeek.value,
        jahr: currentYear.value,
        eintrag: eintrag
      }, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (!response.data.success) {
        toast.error('Fehler beim Speichern: ' + response.data.message);
      }
    } catch (error) {
      console.error('Fehler beim Speichern des Eintrags:', error);
      toast.error('Fehler beim Speichern des Eintrags');
    }
  }

  // KC-Bereich Management
  async function saveKCBereich() {
    if (!kcFormValid.value) return;

    // Saubere Kopie der Formulardaten erstellen ohne Vue-Proxies
    const formData = {
      id: kcBereichForm.value.id,
      kc_nummer: kcBereichForm.value.kc_nummer,
      name: kcBereichForm.value.name,
      beschreibung: kcBereichForm.value.beschreibung
    };

    const action = editingKCBereich.value ? 'update_kc_bereich' : 'create_kc_bereich';

    try {
      const response = await axios.post(`${API_BASE_URL}reinigungsplan.php`, {
        action: action,
        ...formData
      }, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        toast.success(response.data.message);
        await fetchKCBereiche();
        closeKCDialog();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Fehler beim Speichern des KC-Bereichs:', error);
      toast.error('Fehler beim Speichern des KC-Bereichs');
    }
  }

  // Neue Funktion: KC-Bereich-Felder direkt in der Tabelle ändern
  async function updateKCBereichField(kc) {
    try {
      const response = await axios.post(`${API_BASE_URL}reinigungsplan.php`, {
        action: 'update_kc_bereich',
        id: kc.id,
        kc_nummer: kc.kc_nummer,
        name: kc.name,
        beschreibung: kc.beschreibung
      }, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        toast.success(`${kc.name} aktualisiert`);
        await fetchKCBereiche();
      } else {
        toast.error(response.data.message);
        await fetchKCBereiche();
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des KC-Bereichs:', error);
      toast.error('Fehler beim Aktualisieren des KC-Bereichs');
      await fetchKCBereiche();
    }
  }

  function editKCBereich(kc) {
    Object.assign(kcBereichForm.value, kc);
    editingKCBereich.value = true;
  }

  async function deleteKCBereich(id) {
    if (confirm('Sind Sie sicher, dass Sie diesen KC-Bereich löschen möchten?')) {
      try {
        const response = await axios.post(`${API_BASE_URL}reinigungsplan.php`, {
          action: 'delete_kc_bereich',
          id: id
        }, {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        });

        if (response.data.success) {
          toast.success(response.data.message);
          await fetchKCBereiche();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error('Fehler beim Löschen des KC-Bereichs:', error);
        toast.error('Fehler beim Löschen des KC-Bereichs');
      }
    }
  }

  function closeKCDialog() {
    kcBereichDialog.value = false;
    Object.assign(kcBereichForm.value, {
      id: null,
      kc_nummer: '',
      name: '',
      beschreibung: ''
    });
    editingKCBereich.value = false;
  }

  // Dialog-Funktionen
  function openArbeitsbereichDialog() {
    // Sicherstellen, dass das Formular initialisiert ist
    if (!arbeitsbereichForm.value) {
      arbeitsbereichForm.value = {
        id: null,
        kc_bereich_id: null,
        arbeitsbereich_typ_id: null,
        nummer: null,
        name: '',
        mitarbeiter_nummer: null,
        sortierung: null
      };
    }
    // Mitarbeiter-Auswahl zurücksetzen
    selectedMitarbeiter.value = null;
    editingArbeitsbereich.value = false;
    arbeitsbereichFormValid.value = false;
    arbeitsbereichDialog.value = true;
  }

  // Handler für Mitarbeiter-Auswahl-Änderungen
  function onMitarbeiterSelectionChange(value) {
    // Synchronisiere mit dem Formular
    if (arbeitsbereichForm.value) {
      arbeitsbereichForm.value.mitarbeiter_nummer = value;
    }
  }

  // Arbeitsbereich Management
  async function saveArbeitsbereich() {
    // Beim Hinzufügen prüfen wir die Formularvalidierung, beim Bearbeiten nicht
    if (!editingArbeitsbereich.value && !arbeitsbereichFormValid.value) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Saubere Kopie der Formulardaten erstellen ohne Vue-Proxies
    const formData = {
      id: arbeitsbereichForm.value.id,
      kc_bereich_id: activeKC.value,
      arbeitsbereich_typ_id: arbeitsbereichForm.value.arbeitsbereich_typ_id,
      nummer: arbeitsbereichForm.value.nummer,
      name: arbeitsbereichForm.value.name,
      mitarbeiter_nummer: arbeitsbereichForm.value.mitarbeiter_nummer || null,
      sortierung: arbeitsbereichForm.value.sortierung
    };

    const action = editingArbeitsbereich.value ? 'update_arbeitsbereich' : 'create_arbeitsbereich';

    try {
      const response = await axios.post(`${API_BASE_URL}reinigungsplan.php`, {
        action: action,
        ...formData
      }, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        toast.success(response.data.message);
        await fetchKCArbeitsbereiche(activeKC.value);
        closeArbeitsbereichDialog();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Fehler beim Speichern des Arbeitsbereichs:', error);
      toast.error('Fehler beim Speichern des Arbeitsbereichs');
    }
  }

  function editArbeitsbereich(bereich) {
    // Sicherstellen, dass arbeitsbereich_typ_id korrekt gesetzt ist
    const typId = bereich.arbeitsbereich_typ_id || getArbeitsbereichTypIdByName(bereich.arbeitsbereich_typ);

    // Formular mit Daten füllen ohne die reactive reference zu überschreiben
    Object.assign(arbeitsbereichForm.value, {
      ...bereich,
      arbeitsbereich_typ_id: typId,
      // Explizit den Mitarbeiter-Wert setzen
      mitarbeiter_nummer: bereich.mitarbeiter_nummer || null
    });

    // Separaten Mitarbeiter-Wert für die v-select setzen
    selectedMitarbeiter.value = bereich.mitarbeiter_nummer || null;

    editingArbeitsbereich.value = true;

    // Nach dem nächsten Tick sicherstellen, dass das Formular richtig initialisiert ist
    nextTick(() => {
      arbeitsbereichFormValid.value = true;
    });
  }

  // Hilfsfunktion um die Typ-ID anhand des Namens zu finden
  function getArbeitsbereichTypIdByName(typeName) {
    const typ = arbeitsbereichTypen.value.find(t => t.name === typeName);
    return typ ? typ.id : null;
  }

  // Funktion: Arbeitsbereich-Felder direkt in der Tabelle ändern
  async function updateArbeitsbereichField(bereich) {
    try {
      const response = await axios.post(`${API_BASE_URL}reinigungsplan.php`, {
        action: 'update_arbeitsbereich',
        id: bereich.id,
        kc_bereich_id: bereich.kc_bereich_id,
        arbeitsbereich_typ_id: bereich.arbeitsbereich_typ_id,
        nummer: bereich.nummer,
        name: bereich.name,
        mitarbeiter_nummer: bereich.mitarbeiter_nummer || null,
        sortierung: bereich.sortierung
      }, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        toast.success(`${bereich.name} aktualisiert`);
        // Arbeitsbereiche neu laden, um sicherzustellen, dass alles synchron ist
        await fetchKCArbeitsbereiche(activeKC.value);
      } else {
        toast.error(response.data.message);
        // Bei Fehler die Daten neu laden
        await fetchKCArbeitsbereiche(activeKC.value);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Arbeitsbereichs:', error);
      toast.error('Fehler beim Aktualisieren des Arbeitsbereichs');
      // Bei Fehler die Daten neu laden
      await fetchKCArbeitsbereiche(activeKC.value);
    }
  }

  async function deleteArbeitsbereich(id) {
    if (confirm('Sind Sie sicher, dass Sie diesen Arbeitsbereich löschen möchten?')) {
      try {
        const response = await axios.post(`${API_BASE_URL}reinigungsplan.php`, {
          action: 'delete_arbeitsbereich',
          id: id
        }, {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        });

        if (response.data.success) {
          toast.success(response.data.message);
          await fetchKCArbeitsbereiche(activeKC.value);
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error('Fehler beim Löschen des Arbeitsbereichs:', error);
        toast.error('Fehler beim Löschen des Arbeitsbereichs');
      }
    }
  }

  function closeArbeitsbereichDialog() {
    arbeitsbereichDialog.value = false;
    // Formular zurücksetzen ohne die reactive reference zu überschreiben
    Object.assign(arbeitsbereichForm.value, {
      id: null,
      kc_bereich_id: null,
      arbeitsbereich_typ_id: null,
      nummer: null,
      name: '',
      mitarbeiter_nummer: null,
      sortierung: null
    });
    // Mitarbeiter-Auswahl zurücksetzen
    selectedMitarbeiter.value = null;
    editingArbeitsbereich.value = false;
    arbeitsbereichFormValid.value = false;
  }

  // Hilfsfunktionen
  function getArbeitsbereichCount(kcId) {
    return kcArbeitsbereicheCounts.value[kcId] || 0;
  }

  // PDF Export (vereinfacht, basierend auf dynamischen Daten)
  const exportPdf = async () => {
    if (!activeKCData.value) return;

    exporting.value = true;
    toast.info("PDF wird erstellt...");

    try {
      await nextTick();

      // Hier würde die PDF-Erstellung mit den aktuellen dynamischen Daten erfolgen
      // Vereinfachte Version - in der Realität würde man die gleiche Logik wie vorher verwenden,
      // aber mit den dynamischen Daten aus currentArbeitsbereiche.value

      const container = document.createElement('div');
      container.style.width = '1000px';
      container.style.padding = '20px';
      container.style.backgroundColor = '#ffffff';
      container.style.fontFamily = 'Arial, sans-serif';
      container.style.color = '#000000';

      // Header erstellen
      const header = document.createElement('div');
      header.style.textAlign = 'center';
      header.style.marginBottom = '20px';
      header.innerHTML = `
        <h1 style="margin-bottom: 10px; font-size: 20px; font-weight: bold;">REINIGUNGSPLAN ${activeKCData.value.name.toUpperCase()}</h1>
        <div style="margin-bottom: 15px; border-top: 1px solid #000000; border-bottom: 1px solid #000000; padding: 10px;">
          <span style="margin-right: 30px; font-weight: bold;">KW: ${currentWeek.value}</span>
          <span style="font-weight: bold;">Jahr: ${currentYear.value}</span>
        </div>
      `;
      container.appendChild(header);

      // Tabelle mit dynamischen Daten erstellen
      const table = document.createElement('table');
      table.style.width = '100%';
      table.style.borderCollapse = 'collapse';
      table.style.borderSpacing = '0';
      table.style.border = '1px solid #000000';

      // Header-Row
      const thead = document.createElement('thead');
      const headerRow = document.createElement('tr');

      const thArbeitsbereich = document.createElement('th');
      thArbeitsbereich.textContent = 'Arbeitsbereich';
      thArbeitsbereich.style.border = '1px solid #000000';
      thArbeitsbereich.style.padding = '8px';
      thArbeitsbereich.style.textAlign = 'left';
      thArbeitsbereich.style.fontWeight = 'bold';
      headerRow.appendChild(thArbeitsbereich);

      weekDays.value.forEach(day => {
        const th = document.createElement('th');
        th.innerHTML = `${day.name}<br><span style="font-size: 12px; font-weight: normal;">${day.date}</span>`;
        th.style.border = '1px solid #000000';
        th.style.padding = '8px';
        th.style.textAlign = 'center';
        th.style.fontWeight = 'bold';
        headerRow.appendChild(th);
      });

      thead.appendChild(headerRow);
      table.appendChild(thead);

      // Body mit dynamischen Arbeitsbereichen
      const tbody = document.createElement('tbody');

      currentArbeitsbereiche.value.forEach(bereich => {
        const tr = document.createElement('tr');

        const tdName = document.createElement('td');
        tdName.textContent = bereich.name + (bereich.mitarbeiter_nummer ? ` (${bereich.mitarbeiter_nummer})` : '');
        tdName.style.border = '1px solid #000000';
        tdName.style.padding = '8px';
        tdName.style.textAlign = 'left';
        tdName.style.fontWeight = 'bold';
        tr.appendChild(tdName);

        weekDays.value.forEach(day => {
          const td = document.createElement('td');
          const eintrag = getReinigungsEintrag(bereich.id, day.name);
          td.textContent = eintrag;
          td.style.border = '1px solid #000000';
          td.style.padding = '8px';
          td.style.height = '30px';
          tr.appendChild(td);
        });

        tbody.appendChild(tr);
      });

      table.appendChild(tbody);
      container.appendChild(table);

      // Unterschriftentext
      const signatureHint = document.createElement('p');
      signatureHint.textContent = 'Mit Ihrer Unterschrift bestätigen Sie, dass die Reinigungstätigkeiten entsprechend dem Plan durchgeführt wurden.';
      signatureHint.style.marginTop = '20px';
      signatureHint.style.textAlign = 'center';
      signatureHint.style.fontStyle = 'italic';
      signatureHint.style.fontSize = '12px';
      signatureHint.style.color = '#000000';
      container.appendChild(signatureHint);

      // Rest der PDF-Erstellung wie im ursprünglichen Code...
      document.body.appendChild(container);
      container.style.position = 'absolute';
      container.style.top = '-9999px';
      container.style.left = '0';

      const canvas = await html2canvas(container, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });

      document.body.removeChild(container);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });

      const pdfPageWidth = pdf.internal.pageSize.getWidth();
      const pdfPageHeight = pdf.internal.pageSize.getHeight();
      const margin = 40;
      const contentWidth = pdfPageWidth - margin * 2;
      const contentHeight = pdfPageHeight - margin * 2;

      let imgWidth = contentWidth;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight > contentHeight) {
        imgHeight = contentHeight;
        imgWidth = (canvas.width * imgHeight) / canvas.height;
      }

      const xPosition = margin + (contentWidth - imgWidth) / 2;
      pdf.addImage(imgData, 'PNG', xPosition, margin, imgWidth, imgHeight);

      pdf.setFontSize(8);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`${activeKCData.value.name} | KW ${currentWeek.value} | ${currentYear.value}`, margin, 20);

      const today = new Date();
      const formattedDate = today.toLocaleDateString('de-DE');
      pdf.text(`Erstellt am: ${formattedDate}`, margin, pdfPageHeight - 20);
      pdf.text('Seite 1/1', pdfPageWidth - margin - 30, pdfPageHeight - 20);

      const fileName = `Reinigungsplan_${activeKCData.value.kc_nummer}_KW${currentWeek.value}_${currentYear.value}.pdf`;
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');

      toast.success('PDF wurde erfolgreich erstellt und im neuen Tab geöffnet!');
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      toast.error('Beim Erstellen der PDF ist ein Fehler aufgetreten');
    } finally {
      exporting.value = false;
    }
  };

  // Export aller KC-Bereiche als PDF
  const exportAllKCsPdf = async () => {
    if (kcBereiche.value.length === 0) {
      toast.error('Keine KC-Bereiche vorhanden');
      return;
    }

    exportingAll.value = true;
    toast.info("PDF mit allen KC-Bereichen wird erstellt...");

    try {
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });

      const pdfPageWidth = pdf.internal.pageSize.getWidth();
      const pdfPageHeight = pdf.internal.pageSize.getHeight();
      const margin = 40;
      const contentWidth = pdfPageWidth - margin * 2;
      const contentHeight = pdfPageHeight - margin * 2;

      let isFirstPage = true;

      // Für jeden KC-Bereich eine Seite erstellen
      for (const kcBereich of kcBereiche.value) {
        if (!isFirstPage) {
          pdf.addPage();
        }
        isFirstPage = false;

        // Arbeitsbereiche für diesen KC-Bereich laden
        const arbeitsbereicheResponse = await axios.get(`${API_BASE_URL}reinigungsplan.php?action=get_kc_arbeitsbereiche&kc_bereich_id=${kcBereich.id}`, {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        });

        let kcArbeitsbereiche = [];
        if (arbeitsbereicheResponse.data.success) {
          kcArbeitsbereiche = arbeitsbereicheResponse.data.data;
        }

        // Reinigungsplan-Daten für diesen KC-Bereich laden
        const reinigungsResponse = await axios.get(`${API_BASE_URL}reinigungsplan.php?action=get_reinigungsplan_data&kc_bereich_id=${kcBereich.id}&kalenderwoche=${currentWeek.value}&jahr=${currentYear.value}`, {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        });

        let kcReinigungsEintraege = {};
        if (reinigungsResponse.data.success) {
          const entriesMap = {};
          reinigungsResponse.data.data.forEach(entry => {
            const key = `${entry.kc_arbeitsbereich_id}-${entry.tag_name}`;
            entriesMap[key] = entry.eintrag || '';
          });
          kcReinigungsEintraege = entriesMap;
        }

        // Container für diese Seite erstellen
        const container = document.createElement('div');
        container.style.width = '1000px';
        container.style.padding = '20px';
        container.style.backgroundColor = '#ffffff';
        container.style.fontFamily = 'Arial, sans-serif';
        container.style.color = '#000000';

        // Header für diesen KC-Bereich
        const header = document.createElement('div');
        header.style.textAlign = 'center';
        header.style.marginBottom = '20px';
        header.innerHTML = `
          <h1 style="margin-bottom: 10px; font-size: 20px; font-weight: bold;">${kcBereich.name.toUpperCase()}</h1>
          <div style="margin-bottom: 15px; border-top: 1px solid #000000; border-bottom: 1px solid #000000; padding: 10px;">
            <span style="margin-right: 30px; font-weight: bold;">Jahr: ${currentYear.value}</span>
            <span style="font-weight: bold;">KW: ${currentWeek.value}</span>
          </div>
        `;
        container.appendChild(header);

        // Tabelle für diesen KC-Bereich erstellen
        const table = document.createElement('table');
        table.style.width = '100%';
        table.style.borderCollapse = 'collapse';
        table.style.borderSpacing = '0';
        table.style.border = '1px solid #000000';

        // Header-Row
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');

        const thArbeitsbereich = document.createElement('th');
        thArbeitsbereich.textContent = 'Arbeitsbereich';
        thArbeitsbereich.style.border = '1px solid #000000';
        thArbeitsbereich.style.padding = '8px';
        thArbeitsbereich.style.textAlign = 'left';
        thArbeitsbereich.style.fontWeight = 'bold';
        headerRow.appendChild(thArbeitsbereich);

        weekDays.value.forEach(day => {
          const th = document.createElement('th');
          th.innerHTML = `${day.name}<br><span style="font-size: 12px; font-weight: normal;">${day.date}</span>`;
          th.style.border = '1px solid #000000';
          th.style.padding = '8px';
          th.style.textAlign = 'center';
          th.style.fontWeight = 'bold';
          headerRow.appendChild(th);
        });

        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Body mit Arbeitsbereichen für diesen KC
        const tbody = document.createElement('tbody');

        if (kcArbeitsbereiche.length > 0) {
          kcArbeitsbereiche.forEach(bereich => {
            const tr = document.createElement('tr');

            const tdName = document.createElement('td');
            tdName.textContent = bereich.name + (bereich.mitarbeiter_nummer ? ` (${bereich.mitarbeiter_nummer})` : '');
            tdName.style.border = '1px solid #000000';
            tdName.style.padding = '8px';
            tdName.style.textAlign = 'left';
            tdName.style.fontWeight = 'bold';
            tr.appendChild(tdName);

            weekDays.value.forEach(day => {
              const td = document.createElement('td');
              const key = `${bereich.id}-${day.name}`;
              const eintrag = kcReinigungsEintraege[key] || '';
              td.textContent = eintrag;
              td.style.border = '1px solid #000000';
              td.style.padding = '8px';
              td.style.height = '30px';
              tr.appendChild(td);
            });

            tbody.appendChild(tr);
          });
        } else {
          // Leere Zeile wenn keine Arbeitsbereiche vorhanden
          const tr = document.createElement('tr');
          const td = document.createElement('td');
          td.textContent = 'Keine Arbeitsbereiche vorhanden';
          td.style.border = '1px solid #000000';
          td.style.padding = '8px';
          td.style.textAlign = 'center';
          td.style.fontStyle = 'italic';
          td.style.color = '#666666';
          td.colSpan = 7; // 1 für Arbeitsbereich + 6 für Wochentage
          tr.appendChild(td);
          tbody.appendChild(tr);
        }

        table.appendChild(tbody);
        container.appendChild(table);

        // Unterschriftentext
        const signatureHint = document.createElement('p');
        signatureHint.textContent = 'Mit Ihrer Unterschrift bestätigen Sie, dass die Reinigungstätigkeiten entsprechend dem Plan durchgeführt wurden.';
        signatureHint.style.marginTop = '20px';
        signatureHint.style.textAlign = 'center';
        signatureHint.style.fontStyle = 'italic';
        signatureHint.style.fontSize = '12px';
        signatureHint.style.color = '#000000';
        container.appendChild(signatureHint);

        // Container temporär zum DOM hinzufügen für html2canvas
        document.body.appendChild(container);
        container.style.position = 'absolute';
        container.style.top = '-9999px';
        container.style.left = '0';

        const canvas = await html2canvas(container, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          useCORS: true
        });

        document.body.removeChild(container);

        const imgData = canvas.toDataURL('image/png');

        let imgWidth = contentWidth;
        let imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (imgHeight > contentHeight) {
          imgHeight = contentHeight;
          imgWidth = (canvas.width * imgHeight) / canvas.height;
        }

        const xPosition = margin + (contentWidth - imgWidth) / 2;
        pdf.addImage(imgData, 'PNG', xPosition, margin, imgWidth, imgHeight);

        // Footer für diese Seite
        pdf.setFontSize(8);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`${kcBereich.name} | KW ${currentWeek.value} | ${currentYear.value}`, margin, 20);

        const today = new Date();
        const formattedDate = today.toLocaleDateString('de-DE');
        pdf.text(`Erstellt am: ${formattedDate}`, margin, pdfPageHeight - 20);

        const currentPageNum = kcBereiche.value.indexOf(kcBereich) + 1;
        pdf.text(`Seite ${currentPageNum}/${kcBereiche.value.length}`, pdfPageWidth - margin - 50, pdfPageHeight - 20);
      }

      const fileName = `Reinigungsplan_Alle_KC_KW${currentWeek.value}_${currentYear.value}.pdf`;
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');

      toast.success(`PDF mit ${kcBereiche.value.length} KC-Bereichen wurde erfolgreich erstellt!`);
    } catch (error) {
      console.error('Fehler beim PDF-Export aller KC-Bereiche:', error);
      toast.error('Beim Erstellen der PDF ist ein Fehler aufgetreten');
    } finally {
      exportingAll.value = false;
    }
  };

  // Watchers
  watch(activeKC, (newValue) => {
    if (newValue) {
      fetchKCArbeitsbereiche(newValue);
      const kcData = kcBereiche.value.find(kc => kc.id === newValue);
      if (kcData) {
        document.title = `${kcData.name} - KW ${currentWeek.value} - ARCM Core`;
      }
    } else {
      // Fallback-Titel wenn kein KC-Bereich ausgewählt ist
      document.title = 'Reinigungsplan - ARCM Core';
    }
  });

  // Lifecycle
  onMounted(async () => {
    await fetchKCBereiche();
    await fetchArbeitsbereichTypen();
    await fetchMitarbeiter();
  });

  // Cleanup beim Verlassen der Komponente
  onUnmounted(() => {
    // Titel auf Standard zurücksetzen, damit der Route-Watcher in App.vue übernehmen kann
    document.title = 'ARCM Core';
  });
  </script>

  <style scoped>
  /* Verwaltungsbereich */
  .management-buttons {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
  }

  /* Allgemeine Stile */
  .reinigungsplan-container {
    max-width: 1200px;
    margin: 0 auto;
    padding-top: 24px;
    padding-bottom: 32px;
  }

  .reinigungsplan-card {
    background-color: rgba(30, 30, 35, 0.7);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .kc-print-value {
    display: none;
  }

  .week-info {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.9);
  }

  .year-display, .week-display {
    font-weight: 500;
  }

  /* Tabellenstile */
  .table-container {
    overflow-x: auto;
    margin-top: 1rem;
  }

  .reinigungsplan-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1.5rem;
    font-family: 'Roboto', sans-serif;
  }

  .reinigungsplan-table th,
  .reinigungsplan-table td {
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 12px;
    text-align: center;
  }

  .arbeitsbereich-header {
    text-align: left;
    min-width: 150px;
    background-color: rgba(20, 20, 25, 0.5);
  }

  .tag-header {
    background-color: rgba(20, 20, 25, 0.5);
    font-weight: 500;
    min-width: 120px;
  }

  .date-display {
    font-size: 0.8rem;
    font-weight: normal;
    opacity: 0.8;
  }

  .arbeitsbereich-cell {
    text-align: left;
    font-weight: 500;
    background-color: rgba(30, 30, 40, 0.3);
  }

  .mitarbeiter-info {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    font-weight: normal;
  }

  .content-cell {
    background-color: rgba(255, 255, 255, 0.03);
    height: 50px;
  }

  .cell-input {
    width: 100%;
    height: 100%;
    background: transparent;
    border: none;
    text-align: center;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
  }

  .cell-input:focus {
    outline: none;
    background-color: rgba(255, 255, 255, 0.05);
  }

  /* No-Data Bereich */
  .no-data {
    text-align: center;
    padding: 2rem;
    color: rgba(255, 255, 255, 0.7);
  }

  /* Tabs verbessern */
  .v-tabs {
    background: rgba(25, 25, 35, 0.4);
    border-radius: 10px;
    padding: 4px;
    margin-bottom: 20px !important;
  }

  .v-tab {
    border-radius: 8px;
    margin: 0 4px;
    min-width: 200px;
    height: 40px;
    font-weight: 500;
    letter-spacing: 0.3px;
    transition: all 0.3s ease;
  }

  .v-tab--selected {
    background: rgba(25, 118, 210, 0.15);
  }

  /* Export-Buttons aufwerten */
  .export-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 1.5rem;
    gap: 16px;
  }

  .export-btn {
    height: 48px;
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.25) !important;
    letter-spacing: 0.5px;
    border-radius: 10px !important;
    font-weight: 500;
    min-width: 220px;
  }

  .export-btn.v-btn--variant-elevated.v-theme--dark.bg-success {
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.25) !important;
  }

  /* Dialog-Stile */
  .v-dialog .v-card {
    border-radius: 12px;
  }

  .v-list-item {
    border-radius: 8px;
    margin-bottom: 4px;
  }

  /* Arbeitsbereiche-Tabelle im Dialog */
  .arbeitsbereiche-table,
  .kc-bereiche-table {
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    overflow: hidden;
  }

  .arbeitsbereiche-table thead th,
  .kc-bereiche-table thead th {
    background-color: rgba(25, 118, 210, 0.15);
    font-weight: 600;
    padding: 12px;
    text-align: left;
  }

  .arbeitsbereiche-table tbody tr,
  .kc-bereiche-table tbody tr {
    transition: background-color 0.2s ease;
  }

  .arbeitsbereiche-table tbody tr:hover,
  .kc-bereiche-table tbody tr:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .arbeitsbereiche-table tbody td,
  .kc-bereiche-table tbody td {
    padding: 8px 12px;
  }

  /* Print-Stile */
  @media print {
    .no-print {
      display: none !important;
    }

    .print-only {
      display: block !important;
    }

    .kc-print-value {
      display: inline !important;
    }
  }
  </style>
